<script setup lang="ts">
import type { ImagePlaceholderVariant } from "../utils/imagePlaceholder";

const props = withDefaults(
  defineProps<{
    src?: string | null;
    alt?: string;
    variant?: ImagePlaceholderVariant;
    imgClass?: string;
  }>(),
  { src: "", alt: "", variant: "generic", imgClass: "size-full object-cover" }
);

const failed = ref(false);

const imageRef = useTemplateRef<HTMLImageElement>("image");
const fullyDecoded = ref(true);

function syncFullyDecoded() {
  const image = imageRef.value;
  fullyDecoded.value = Boolean(image?.complete && image.naturalWidth > 0);
}

onMounted(syncFullyDecoded);

watch(
  () => props.src,
  async () => {
    failed.value = false;
    fullyDecoded.value = false;
    await nextTick();
    syncFullyDecoded();
  }
);

const online = useAppNetworkOnline();
watch(online, (isOnline) => {
  if (isOnline) failed.value = false;
});
</script>

<template>
  <img
    v-if="src && !failed"
    ref="image"
    :src="src"
    :alt="alt"
    loading="lazy"
    decoding="async"
    class="motion-safe:transition-opacity motion-safe:duration-200"
    :class="[imgClass, fullyDecoded ? 'opacity-100' : 'opacity-0']"
    @load="fullyDecoded = true"
    @error="failed = true"
  >
  <slot v-else name="placeholder">
    <AppImagePlaceholder :variant="variant" class="size-full" />
  </slot>
</template>
